import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { createPortal } from 'react-dom';

const WeeklyReviewCard = ({ content, timestamp }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    
    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isExpanded]);
  
  // Extract the markdown content from the weekly_review tag
  const extractReviewContent = (text) => {
    const match = text.match(/<weekly_review>([\s\S]*?)<\/weekly_review>/);
    return match ? match[1].trim() : text;
  };
  
  const reviewContent = extractReviewContent(content);
  
  // Get preview content (first few lines)
  const getPreview = () => {
    const lines = reviewContent.split('\n');
    const previewLines = [];
    let charCount = 0;
    
    for (const line of lines) {
      if (charCount + line.length > 200) break;
      if (line.startsWith('# ')) {
        previewLines.push(line.replace('# ', ''));
      } else if (line.startsWith('## ')) {
        if (previewLines.length > 1) break;
        previewLines.push(line.replace('## ', ''));
      } else if (line.trim() && !line.startsWith('#')) {
        previewLines.push(line);
      }
      charCount += line.length;
    }
    
    return previewLines.join(' • ').substring(0, 150) + '...';
  };
  
  return (
    <>
      {/* Preview Card */}
      <div
        className="group relative bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-600/30 rounded-xl p-4 mb-4 cursor-pointer hover:from-purple-900/30 hover:to-blue-900/30 hover:border-purple-500/40 transition-all duration-200 hover:shadow-lg"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">📊</span>
            <div>
              <h3 className="text-sm font-semibold text-purple-200">Weekly Training Review</h3>
              <p className="text-xs text-purple-300/70 mt-0.5">{timestamp}</p>
            </div>
          </div>
          <button className="text-purple-300/50 hover:text-purple-200 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
        
        <div className="text-sm text-gray-300/80 line-clamp-3">
          {getPreview()}
        </div>
        
        <div className="mt-3 flex items-center gap-4 text-xs text-purple-300/60">
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Full Review
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Progress Analysis
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Action Items
          </span>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl pointer-events-none"></div>
      </div>
      
      {/* Expanded Modal - Full Screen using Portal */}
      {isExpanded && createPortal(
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[9999] animate-fade-in"
          onClick={() => setIsExpanded(false)}
        >
          <div 
            className="bg-gray-900 w-full h-full overflow-hidden shadow-2xl relative flex flex-col animate-float-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Fixed at top */}
            <div className="bg-gradient-to-r from-purple-900/80 to-blue-900/80 backdrop-blur-sm px-6 py-5 border-b border-purple-600/30 flex-shrink-0">
              <div className="flex items-center justify-between max-w-6xl mx-auto">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">📊</span>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Weekly Training Review</h2>
                    <p className="text-sm text-purple-200/70 mt-1">{timestamp}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsExpanded(false)}
                  className="text-gray-300 hover:text-white transition-colors p-3 hover:bg-white/10 rounded-xl"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto bg-gray-900">
              <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="prose prose-invert prose-purple max-w-none prose-lg">
                  <ReactMarkdown
                    components={{
                      h1: ({children}) => <h1 className="text-3xl font-bold text-purple-200 mb-6 mt-8">{children}</h1>,
                      h2: ({children}) => <h2 className="text-2xl font-semibold text-purple-300 mt-8 mb-4 pb-2 border-b border-purple-800/50">{children}</h2>,
                      h3: ({children}) => <h3 className="text-xl font-medium text-purple-400 mt-6 mb-3">{children}</h3>,
                      p: ({children}) => <p className="text-gray-300 leading-relaxed mb-4 text-base">{children}</p>,
                      ul: ({children}) => <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 ml-4">{children}</ul>,
                      ol: ({children}) => <ol className="list-decimal list-inside text-gray-300 space-y-2 mb-4 ml-4">{children}</ol>,
                      li: ({children}) => <li className="text-gray-300 leading-relaxed">{children}</li>,
                      strong: ({children}) => <strong className="font-semibold text-purple-200">{children}</strong>,
                      hr: () => <hr className="border-gray-700 my-6" />,
                      code: ({children}) => <code className="bg-gray-800 text-purple-200 px-2 py-1 rounded text-sm">{children}</code>,
                    }}
                  >
                    {reviewContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
            
            {/* Modal Footer - Fixed at bottom */}
            <div className="bg-gray-900/90 px-6 py-4 border-t border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between max-w-6xl mx-auto">
                <div className="text-sm text-gray-400">
                  Review generated for your training week
                </div>
                <button 
                  onClick={() => setIsExpanded(false)}
                  className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium shadow-lg"
                >
                  Close Review
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default WeeklyReviewCard;